import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as permanentLicensesService from '../services/permanentLicenses';
import { userConfigsSelectors } from './userConfigs';
import { entitiesAdapter } from './common/adapter';

const initialState = {
  isFetching: false,
  isCreating: false,
  isUpdating: false,
  isChanging: false,
  isRemoving: false,
  isRestoring: false,
  isAssignmentInProcess: false,

  creatingUpdatingChangingError: null,
  fetchingError: null,
  removingRestoringError: null,
  assignmentError: null,
  downloadError: null
};

const sliceName = 'permanentLicenses';

export const fetchAll = createAsyncThunk(
  `${sliceName}/fetchAll`,
  (args, thunkAPI) => {
    return permanentLicensesService.getPermanentLicenses()
      .catch((error) => {
        error.clientMessage = "Can't get permanentLicenses from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchById = createAsyncThunk(
  `${sliceName}/fetchById`,
  (args, thunkAPI) => {
    return permanentLicensesService.getPermanentLicenseById(args.id)
      .catch((error) => {
        error.clientMessage = "Can't get permanentLicense from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const create = createAsyncThunk(
  `${sliceName}/create`,
  (args, thunkAPI) => {
    return permanentLicensesService.createNewPermanentLicenses(
      args.clientId, args.productName, args.type, args.features, args.supportDayCount, args.licenseCount
    )
      .catch((error) => {
        error.clientMessage = "Can't post permanentLicenses to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const update = createAsyncThunk(
  `${sliceName}/update`,
  (args, thunkAPI) => {
    return permanentLicensesService.updatePermanentLicense(
      args.licenseId, args.features, args.supportDayCount
    )
      .catch((error) => {
        error.clientMessage = "Can't update permanentLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const change = createAsyncThunk(
  `${sliceName}/change`,
  (args, thunkAPI) => {
    return permanentLicensesService.changePermanentLicense(
      args.productName, args.type, args.licenseId, args.features, args.supportDayCount
    )
      .catch((error) => {
        error.clientMessage = "Can't change permanentLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const restore = createAsyncThunk(
  `${sliceName}/restore`,
  (args, thunkAPI) => {
    return permanentLicensesService
      .restorePermanentLicenses(args.licenseIds)
      .catch((error) => {
        error.clientMessage = "Can't restore permanentLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
)

export const remove = createAsyncThunk(
  `${sliceName}/remove`,
  (args, thunkAPI) => {
    return permanentLicensesService
      .removePermanentLicenses(args.licenseIds)
      .catch((error) => {
        error.clientMessage = "Can't remove permanentLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const deactivate = createAsyncThunk(
  `${sliceName}/deactivate`,
  (args, thunkAPI) => {
    return permanentLicensesService
      .deactivatePermanentLicense(args.licenseId)
      .catch((error) => {
        error.clientMessage = "Can't deactivate permanentLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchByKey = createAsyncThunk(
  `${sliceName}/fetchByKey`,
  (args, thunkAPI) => {
    return permanentLicensesService
      .getPermanentLicenseByKey(args.licenseKey)
      .catch((error) => {
        error.clientMessage = "Can't get permanentLicense from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const assignToClient = createAsyncThunk(
  `${sliceName}/assignToClient`,
  (args, thunkAPI) => {
    return permanentLicensesService
      .assignLicenseToClient(args.licenseId, args.clientId)
      .catch((error) => {
        error.clientMessage = "Can't get permanentLicense from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const reactivate = createAsyncThunk(
  `${sliceName}/reactivate`,
  (args, thunkAPI) => {
    return permanentLicensesService
      .reactivatePermanentLicense(args.licenseId, args.deviceKey,)
      .catch((error) => {
        error.clientMessage = "Can't reactivate permanentLicense";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const activate = createAsyncThunk(
  `${sliceName}/activate`,
  (args, thunkAPI) => {
    return permanentLicensesService
      .activateLicense(args.licenseId, args.deviceKey)
      .catch((error) => {
        error.clientMessage = "Can't activate permanentLicense";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const download = createAsyncThunk(
  `${sliceName}/download`,
  (args, thunkAPI) => {
    return permanentLicensesService
      .downloadLicense(args.licenseId)
      .catch((error) => {
        error.clientMessage = "Can't download permanentLicenses";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const addToFavorites = createAsyncThunk(
  `${sliceName}/favoritesAdd`,
  (args) => {
    return permanentLicensesService.addPermanentLicenseToFavorites(args.id)
  }
);
export const removeFromFavorites = createAsyncThunk(
  `${sliceName}/favoritesRemove`,
  (args) => {
    return permanentLicensesService.removePermanentLicenseFromFavorites(args.id)
  }
);

const permanentLicenses = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: {
    [fetchAll.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchById.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [create.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [update.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [change.fulfilled]: (state, action) => {
      state.isChanging = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [restore.fulfilled]: (state, action) => {
      state.isRestoring = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [remove.fulfilled]: (state, action) => {
      state.isRemoving = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [deactivate.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [fetchByKey.fulfilled]: (state, action) => {
      state.isFetching = false;
      if (action.payload) {
        entitiesAdapter.upsertOne(state, action);
      }
    },
    [assignToClient.fulfilled]: (state, action) => {
      state.isAssignmentInProcess = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [reactivate.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [activate.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [addToFavorites.fulfilled]: entitiesAdapter.upsertOne,
    [removeFromFavorites.fulfilled]: entitiesAdapter.upsertOne,

    [fetchAll.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
      state.creatingUpdatingChangingError = null;
      state.removingRestoringError = null;
    },
    [fetchById.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [create.pending]: (state) => {
      state.isCreating = true;
      state.creatingUpdatingChangingError = null;
    },
    [update.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingChangingError = null;
    },
    [change.pending]: (state) => {
      state.isChanging = true;
      state.creatingUpdatingChangingError = null;
    },
    [restore.pending]: (state) => {
      state.isRestoring = true;
      state.removingRestoringError = null;
    },
    [remove.pending]: (state) => {
      state.isRemoving = true;
      state.removingRestoringError = null
    },
    [deactivate.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingChangingError = null;
    },
    [fetchByKey.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [assignToClient.pending]: (state) => {
      state.isAssignmentInProcess = true;
      state.assignmentError = null;
    },
    [reactivate.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingChangingError = null;
    },
    [activate.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingChangingError = null;
    },
    [download.pending]: (state) => {
      state.downloadError = null;
    },

    [fetchAll.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [fetchById.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [create.rejected]: (state, action) => {
      state.isCreating = false;
      state.creatingUpdatingChangingError = action.payload;
    },
    [update.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingChangingError = action.payload;
    },
    [change.rejected]: (state, action) => {
      state.isChanging = false;
      state.creatingUpdatingChangingError = action.payload;
    },
    [restore.rejected]: (state, action) => {
      state.isRestoring = false;
      state.removingRestoringError = action.payload;
    },
    [remove.rejected]: (state, action) => {
      state.isRemoving = false;
      state.removingRestoringError = action.payload;
    },
    [deactivate.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingChangingError = action.payload;
    },
    [fetchByKey.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [assignToClient.rejected]: (state, action) => {
      state.isAssignmentInProcess = false;
      state.assignmentError = action.payload;
    },
    [reactivate.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingChangingError = action.payload;
    },
    [activate.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingChangingError = action.payload;
    },
    [download.rejected]: (state, action) => {
      state.downloadError = action.payload;
    },
  }
});

const { reducer } = permanentLicenses;

const permanentLicensesAdapterSelectors = entitiesAdapter.getSelectors((state) => state[sliceName]);

const selectedLicensesSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => permanentLicensesAdapterSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id]),
)

export const permanentLicensesSelectors = {
  ...permanentLicensesAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  isCreating: (state) => state[sliceName].isCreating,
  isUpdating: (state) => state[sliceName].isUpdating,
  isChanging: (state) => state[sliceName].isChanging,
  isRemoving: (state) => state[sliceName].isRemoving,
  isRestoring: (state) => state[sliceName].isRestoring,
  isAssignmentInProcess: (state) => state[sliceName].isAssignmentInProcess,

  fetchingError: (state) => state[sliceName].fetchingError,
  creatingUpdatingChangingError: (state) => state[sliceName].creatingUpdatingChangingError,
  removingRestoringError: (state) => state[sliceName].removingRestoringError,
  assignmentError: (state) => state[sliceName].assignmentError,
  downloadError: (state) => state[sliceName].downloadError,

  state: (state) => state[sliceName],
  selectedLicenses: selectedLicensesSelector,
};

export default reducer;



// WEBPACK FOOTER //
// ./src/store/permanentLicensesSlice.js